/**
 * Port of the old app's on-the-fly answer presenter (server.py:
 * renumberAnswer, endnoteParts, renderEndnotes).
 *
 * - renumberAnswer: move [N]-markers after sentence-ending punctuation,
 *   collapse adjacent markers, renumber sequentially → endnote mapping
 * - citeLine: build the "Författare, *Titel*, SvJT 2016 nr 9 s. X" line
 *   for one source hit
 */

export const JOURNAL_NAMES: Record<string, string> = {
  svjt: "Svensk Juristtidning",
  jt: "Juridisk Tidskrift",
  ert: "Europarättslig tidskrift",
  fvr: "Förvaltningsrättslig tidskrift",
};

export interface SourceHit {
  source_id?: string | null;
  title?: string | null;
  authors?: string[] | null;
  year?: number | null;
  issue?: string | null;
  landing_url?: string | null;
  kind?: string | null;
  marker?: string | null;
  pincite?: string | null;
  page_start?: number | null;
  text?: string | null;
}

/**
 * Normalise the LLM's sloppy line breaks: trailing-two-space breaks become
 * paragraph breaks, and blank lines are inserted before list items,
 * headings, blockquotes and the start of a table that follow a non-empty
 * line. Never inserts between consecutive table rows (that would split the
 * table into single-row paragraphs for GFM).
 */
export function tidyMarkdown(src: string): string {
  if (!src) return "";
  let s = src;
  s = s.replace(/[ \t]{2,}\n/g, "\n\n");
  s = s.replace(/([^\n])\n(\s*(?:\d+\.|[-*])\s+)/g, "$1\n\n$2");
  s = s.replace(/([^\n])\n(#{1,6}\s+)/g, "$1\n\n$2");
  s = s.replace(/([^\n])\n(>\s)/g, "$1\n\n$2");
  // Blank line before the first table row only.
  const lines = s.split("\n");
  for (let i = 1; i < lines.length; i++) {
    const prev = lines[i - 1];
    const cur = lines[i];
    if (cur.trimStart().startsWith("|") && !prev.includes("|") && prev.trim() !== "") {
      lines.splice(i, 0, "");
      i++;
    }
  }
  return lines.join("\n");
}

/**
 * Port of server.py renumberAnswer: lift [N] out of parentheses, move
 * marker groups after sentence-ending punctuation, collapse adjacent
 * markers, renumber sequentially. Returns { text, endnotes } where each
 * endnote is an array of original source indexes (1-based).
 */
export function renumberAnswer(
  text: string,
  sourcesLen: number,
): { text: string; endnotes: number[][] } {
  if (!text) return { text: "", endnotes: [] };
  let t = text.replace(/“/g, "”");
  // Some models emit "[N25]" / "[N 25]" instead of "[25]". Normalise to the
  // plain form so the renumber pass below (and the endnote links) see it.
  t = t.replace(/\[[Nn]\s*(\d{1,3})\]/g, "[$1]");
  // Lift out of parentheses: "(jfr [1])" → "(jfr)[1]"; "([1])" → "[1]".
  t = t.replace(/\(\s*\[(\d{1,3})\]\s*\)/g, "[$1]");
  t = t.replace(/\(([^()\n]*?)\s+\[(\d{1,3})\]\s*\)/g, "($1)[$2]");
  // Move [N]-group after punctuation: "X [1]." / "X[1]." → "X.[1]".
  t = t.replace(/([^.!?:;\s])\s*(\[\d{1,3}\](?:\s*\[\d{1,3}\])*)\s*([.!?:;])/g, "$1$3$2");
  const endnotes: number[][] = [];
  const out = t.replace(/\[\d{1,3}\](?:\s*\[\d{1,3}\])*/g, (run) => {
    const ids = Array.from(run.matchAll(/\[(\d{1,3})\]/g))
      .map((m) => parseInt(m[1], 10))
      .filter((n) => n >= 1 && (!sourcesLen || n <= sourcesLen));
    if (!ids.length) return run;
    const unique = Array.from(new Set(ids));
    endnotes.push(unique);
    return `[${endnotes.length}]`;
  });
  return { text: out, endnotes };
}

function esc(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

/**
 * Deep link to the exact journal page on svjt.se: each printed page has its
 * own URL (/svjt/<år>/<sidnummer>), and svjt.se drops "#page-N" fragments on
 * some pages — so the robust form is the direct page URL. The pincite
 * (e.g. "s. 962") is the page the reader actually wants; fall back to
 * page_start; else the bare landing URL.
 */
export function svjtDeepLink(
  landingUrl: string | null | undefined,
  sourceId: string | null | undefined,
  page: number | null | undefined,
  pincite?: string | null,
): string | null {
  if (sourceId === "svjt" && landingUrl) {
    const target = pincitePage(pincite) ?? page;
    const m = landingUrl.match(/\/svjt\/(\d{4})\/(\d+)$/);
    const articleStart = m ? parseInt(m[2], 10) : null;
    // A pincite may point into the cited source (e.g. an NJA page) rather
    // than this svjt article; only treat it as a page in this article when it
    // falls within the article's span (SvJT articles are rarely > 80 pages).
    if (
      m && target && articleStart !== null &&
      target >= articleStart && target <= articleStart + 80
    ) {
      return landingUrl.replace(/\d+$/, String(target));
    }
    return landingUrl;
  }
  return landingUrl ?? null;
}

/** First page number in a pincite like "p. 962", "s. 62 f.", "962" — or null. */
function pincitePage(pincite: string | null | undefined): number | null {
  if (!pincite) return null;
  const m = pincite.match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
}

/**
 * Plain-text variant of citeLine for export files (no HTML, markdown
 * italics) — feeds the "## Noter" section of /export/docx.
 */
export function citeLinePlain(h: SourceHit): { text: string; url: string | null } {
  const auth = (h.authors || []).join(", ");
  const title = h.title || "(utan titel)";
  const year = h.year || "";
  let line: string;
  if (h.source_id === "svjt") {
    const m = (h.landing_url || "").match(/\/(\d+)$/);
    const startPage = m ? parseInt(m[1], 10) : null;
    let where = "";
    if (h.kind === "footnote" && h.marker) where = `, fn ${h.marker}`;
    else if (h.page_start && (!startPage || h.page_start !== startPage)) where = `, s. ${h.page_start}`;
    line =
      (auth ? auth + ", " : "") +
      `*${title}*, SvJT ${year}${startPage ? ` s. ${startPage}` : ""}${where}`;
  } else {
    const sid = (h.source_id ?? "") || "";
    const journal = JOURNAL_NAMES[sid] || sid;
    const issue = h.issue ? ` nr ${h.issue}` : "";
    line =
      (auth ? auth + ", " : "") +
      `*${title}*` +
      (journal ? `, ${journal}` : "") +
      (year ? ` ${year}` : "") +
      issue +
      (h.kind === "footnote" && h.marker ? `, fn ${h.marker}` : h.page_start ? `, s. ${h.page_start}` : "");
  }
  return { text: line, url: svjtDeepLink(h.landing_url, h.source_id, h.page_start, h.pincite) };
}

/**
 * Map a "/cited-by" citation row (the "Visa källor" list) to the SourceHit
 * shape citeLine / citeLinePlain expect.
 */
export function citationToSourceHit(c: {
  source_id?: string | null;
  title?: string | null;
  authors?: string[] | null;
  year?: number | null;
  issue?: string | null;
  landing_url?: string | null;
  src_kind?: string | null;
  marker?: string | null;
  pincite?: string | null;
  page_start?: number | null;
}): SourceHit {
  return {
    source_id: c.source_id,
    title: c.title,
    authors: c.authors,
    year: c.year,
    issue: c.issue,
    landing_url: c.landing_url,
    kind: c.src_kind === "footnote" ? "footnote" : null,
    marker: c.marker,
    pincite: c.pincite,
    page_start: c.page_start,
  };
}

/**
 * Build the export markdown: the renumbered answer, then (when provided) the
 * full "## Samtliga källor" list — the deterministic "Visa källor" source
 * list, one line per document — and finally a "## Noter" section with one
 * numbered entry per endnote group. The docx exporter turns the Noter
 * section into real Word footnotes; the Samtliga källor section must come
 * BEFORE it in the markdown so it stays in the document body.
 */
export function buildExportMarkdown(
  answer: string,
  sources: { hit: SourceHit }[],
  allSources?: SourceHit[],
): string {
  const { text, endnotes } = renumberAnswer(tidyMarkdown(answer), sources.length);
  const lines = [text.trim()];
  if (allSources && allSources.length) {
    lines.push("", "## Samtliga källor", "");
    allSources.forEach((h) => {
      const { text } = citeLinePlain(h);
      if (text) lines.push(`- ${text}.`);
    });
  }
  if (sources.length && endnotes.length) {
    lines.push("", "## Noter", "");
    endnotes.forEach((origIds, idx) => {
      const ctxs = origIds.map((n) => sources[n - 1]).filter(Boolean);
      if (!ctxs.length) return;
      const segments = ctxs.map((ctx) => citeLinePlain(ctx.hit).text + ".");
      lines.push(`${idx + 1}. ${segments.join("; ")}`);
    });
  }
  return lines.join("\n");
}

/** Filesystem-safe slug for download names (port of the old app). */
export function safeFilename(s: string): string {
  return (s || "svar")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

/**
 * Build the "Författare, <em>Titel</em>, SvJT 2016 [nr 9] s. X / fn Y" line
 * for one source hit. Port of server.py endnoteParts with the svjt
 * special-case (start page derived from landing_url, abbreviated journal).
 */
export function citeLine(h: SourceHit): { html: string; url: string | null } {
  const auth = (h.authors || []).join(", ");
  const title = h.title || "(utan titel)";
  const year = h.year || "";
  let line: string;
  if (h.source_id === "svjt") {
    const m = (h.landing_url || "").match(/\/(\d+)$/);
    const startPage = m ? parseInt(m[1], 10) : null;
    let where = "";
    if (h.kind === "footnote" && h.marker) where = `, fn ${h.marker}`;
    else if (h.page_start && (!startPage || h.page_start !== startPage)) where = `, s. ${h.page_start}`;
    line =
      (auth ? esc(auth) + ", " : "") +
      `<em>${esc(title)}</em>, SvJT ${year}${startPage ? ` s. ${startPage}` : ""}${where}`;
  } else {
    const sid = ((h.source_id ?? "") as string) || "";
    const journal = JOURNAL_NAMES[sid] || sid;
    const issue = h.issue ? ` nr ${h.issue}` : "";
    line =
      (auth ? esc(auth) + ", " : "") +
      `<em>${esc(title)}</em>` +
      (journal ? `, ${esc(journal)}` : "") +
      (year ? ` ${year}` : "") +
      issue +
      (h.kind === "footnote" && h.marker ? `, fn ${esc(h.marker)}` : h.page_start ? `, s. ${h.page_start}` : "");
  }
  return { html: line, url: svjtDeepLink(h.landing_url, h.source_id, h.page_start, h.pincite) };
}